import { useMemo, useReducer, useRef, useEffect, useCallback } from "react";
import isEqual from "lodash/isEqual";
import { VivoPlotProps } from "@/components/VivoPlots/types";
import { SortItem } from "@/components/UI/TableToolbar/Sorting/types";
import { useUser } from "@/contexts/UserContext";
import { sanitizePlotsRuntimeFields } from "@/utils/workspaceConfig";
import {
  normalizeColumns,
  normalizeFilters,
  normalizeSorting,
  createDefaultTableState,
} from "@/utils/workspaceState";
import {
  TableState,
  ColumnsType,
  PlotLayoutsByBreakpoint,
} from "@/types/tableState.types";
import {
  getCodedUserKey,
  getLegacyWorkspaceDraftStorageKey,
  getOlderLegacyWorkspaceDraftStorageKey,
  getWorkspaceDraftStorageKey,
  getWorkspaceUserStorageKey,
  readMigratedLocalStorageItem,
  removeMalformedWorkspaceStorageItem,
} from "@/utils/workspacePersistence";

const currentFilterVersion = "2.0.0";

type FilterAction = {
  type: string;

  value?: any;
  filters?: TableState["filters"];
  columns?: ColumnsType[];
  sorting?: SortItem[];
  lockedColumns?: number;
  initialState?: TableState;
  pagination?: {
    current?: number;
    pageSize?: number;
    total?: number;
  };
  plots?: VivoPlotProps[];
  plotLayoutsByBreakpoint?: PlotLayoutsByBreakpoint;
  screenView?: string;
  workspaceId?: number;
  workspaceModifiedAt?: string;
};

type PersistedTableState = Partial<TableState> & {
  version?: string;
};

const createPersistedTableDraft = (state: TableState): PersistedTableState => ({
  filters: state.filters,
  columns: state.columns,
  lockedColumns: state.lockedColumns,
  sorting: state.sorting,
  pagination: {
    current: state.pagination.current,
    pageSize: state.pagination.pageSize,
  },
  plots: sanitizePlotsRuntimeFields(state.plots),
  plotLayoutsByBreakpoint: state.plotLayoutsByBreakpoint,
  screenView: state.screenView,
  version: currentFilterVersion,
  workspaceId: state.workspaceId,
  workspaceModifiedAt: state.workspaceModifiedAt,
});

const hasCompatibleWorkspaceMeta = (
  savedState: PersistedTableState,
  initialState: TableState,
) => {
  if (!savedState.workspaceId || !savedState.workspaceModifiedAt) return false;
  if (!initialState.workspaceId || !initialState.workspaceModifiedAt)
    return false;

  return (
    savedState.workspaceId === initialState.workspaceId &&
    savedState.workspaceModifiedAt === initialState.workspaceModifiedAt
  );
};

const hydratePersistedTableState = (
  initialState: TableState,
  persistedState: PersistedTableState,
  protectedFilterKeys: string[],
  metadataColumns?: string[],
  metadataFilterKeys?: string[],
): TableState => {
  const defaultState = createDefaultTableState(metadataColumns);
  const sourceFilters = normalizeFilters(
    persistedState.filters,
    metadataFilterKeys,
    metadataColumns,
  );
  const sourceColumns = normalizeColumns(
    persistedState.columns,
    metadataColumns,
  );
  const sourceSorting = normalizeSorting(
    persistedState.sorting,
    metadataColumns,
  );

  const preservedProtectedFilters = Object.fromEntries(
    Object.entries(initialState.filters || {}).filter(([key]) =>
      protectedFilterKeys.includes(key),
    ),
  );

  const filters =
    Object.keys(sourceFilters).length > 0
      ? { ...sourceFilters, ...preservedProtectedFilters }
      : initialState.filters || defaultState.filters;

  const columns =
    sourceColumns.length > 0
      ? sourceColumns
      : initialState.columns || defaultState.columns;

  const sorting =
    sourceSorting.length > 0
      ? sourceSorting
      : initialState.sorting || defaultState.sorting;

  const plots =
    persistedState.plots === undefined
      ? initialState.plots || defaultState.plots
      : sanitizePlotsRuntimeFields(
          persistedState.plots,
          metadataColumns,
          metadataFilterKeys,
        );

  return {
    ...initialState,
    filters,
    columns,
    lockedColumns:
      persistedState.lockedColumns ??
      initialState.lockedColumns ??
      defaultState.lockedColumns,
    sorting,
    pagination:
      persistedState.pagination ||
      initialState.pagination ||
      defaultState.pagination,
    plots,
    plotLayoutsByBreakpoint:
      persistedState.plotLayoutsByBreakpoint ??
      initialState.plotLayoutsByBreakpoint ??
      defaultState.plotLayoutsByBreakpoint,
    screenView:
      persistedState.screenView ||
      initialState.screenView ||
      defaultState.screenView,
    version: currentFilterVersion,
    workspaceId:
      persistedState.workspaceId ?? initialState.workspaceId ?? undefined,
    workspaceModifiedAt:
      persistedState.workspaceModifiedAt ??
      initialState.workspaceModifiedAt ??
      undefined,
  };
};

const filterReducer = (state: TableState, action: FilterAction): TableState => {
  switch (action.type) {
    case "setWorkspaceMeta": {
      return {
        ...state,
        workspaceId: action.workspaceId,
        workspaceModifiedAt: action.workspaceModifiedAt,
      };
    }
    case "setScreenView": {
      return { ...state, screenView: action.screenView };
    }
    case "setAllFilters":
      return {
        ...state,
        filters: { ...action.filters },
        pagination: { ...state.pagination, current: 1 }, // Reset to first page when filters change
      };
    case "setFilter":
      return {
        ...state,
        filters: { ...state.filters, ...action.filters },
        pagination: { ...state.pagination, current: 1 }, // Reset to first page when filters change
      };
    case "setColumn": {
      return { ...state, columns: action.columns ?? state.columns };
    }
    case "setSorting": {
      return {
        ...state,
        sorting: action.sorting,
        pagination: { ...state.pagination, current: 1 },
      };
    }
    case "setLockedColumns": {
      return {
        ...state,
        lockedColumns: action.lockedColumns ?? state.lockedColumns,
      };
    }
    case "setPagination": {
      return {
        ...state,
        pagination: {
          ...state.pagination,
          ...action.pagination,
        },
      };
    }
    case "setPlots": {
      return {
        ...state,
        plots: action.plots,
      };
    }
    case "setPlotLayoutsByBreakpoint": {
      return {
        ...state,
        plotLayoutsByBreakpoint: action.plotLayoutsByBreakpoint,
      };
    }

    case "reset":
      return action.initialState ?? state;
    default:
      return state;
  }
};

const useTableState = ({
  initialState,
  pageName,
  isSingleSubject,
  trialId,
  metadataColumns,
  metadataFilterKeys,
}: {
  initialState: TableState;
  pageName: string;
  isSingleSubject: boolean;
  trialId: string;
  metadataColumns?: string[];
  metadataFilterKeys?: string[];
}) => {
  const protectedFilterKeys = useMemo(
    () => (isSingleSubject ? ["subject"] : []),
    [isSingleSubject],
  );
  const { userInfo } = useUser();
  const userKey = getWorkspaceUserStorageKey(userInfo);
  const codedEmail = getCodedUserKey(userInfo?.email);
  const hasKnownUser = userKey !== "unknown-user";
  const storageKey = getWorkspaceDraftStorageKey(trialId, pageName, userKey);
  const legacyDraftStorageKey = getLegacyWorkspaceDraftStorageKey(
    trialId,
    pageName,
    codedEmail,
  );
  const olderLegacyStorageKey = getOlderLegacyWorkspaceDraftStorageKey(
    pageName,
    codedEmail,
  );

  const savedInitialState = hasKnownUser
    ? readMigratedLocalStorageItem(
        storageKey,
        legacyDraftStorageKey,
        olderLegacyStorageKey,
      )
    : null;

  let parsedSavedInitialState: PersistedTableState = {};
  try {
    const hasMalformedSavedState = removeMalformedWorkspaceStorageItem(
      storageKey,
      savedInitialState,
    );
    parsedSavedInitialState =
      savedInitialState && !hasMalformedSavedState
        ? JSON.parse(savedInitialState)
        : {};
  } catch (error) {
    console.warn(
      `[useTableState] Failed to parse persisted state for "${storageKey}", clearing it.`,
      error,
    );
    parsedSavedInitialState = {};
  }
  const migratedSavedInitialState = parsedSavedInitialState;

  const parsedInitialState: PersistedTableState = useMemo(
    () =>
      migratedSavedInitialState.version === currentFilterVersion
        ? migratedSavedInitialState
        : {},
    [migratedSavedInitialState],
  );

  const hasCompatibleDraft =
    migratedSavedInitialState.version === currentFilterVersion &&
    hasCompatibleWorkspaceMeta(parsedInitialState, initialState);

  // The state the hook would start from if there were no local draft at all
  // (i.e. straight from the workspace/props). Used as the baseline for
  // deciding whether the current state is an "unsaved draft" worth
  // persisting — comparing against `definedInitialState` instead would
  // always read as "no draft" immediately after hydrating from one.
  const freshInitialState: TableState = useMemo(
    () => ({
      ...initialState,
      filters: initialState.filters || {},
      columns:
        initialState.columns ||
        createDefaultTableState(metadataColumns).columns,
      lockedColumns: initialState.lockedColumns ?? 0,
      sorting: initialState.sorting || [],
      pagination:
        initialState.pagination ||
        createDefaultTableState(metadataColumns).pagination,
      plots: initialState.plots || [],
      plotLayoutsByBreakpoint: initialState.plotLayoutsByBreakpoint || {},
      screenView: initialState.screenView || "table",
      version: currentFilterVersion,
      workspaceId: initialState.workspaceId,
      workspaceModifiedAt: initialState.workspaceModifiedAt,
    }),
    [initialState, metadataColumns],
  );

  const definedInitialState = useMemo(
    () =>
      hasCompatibleDraft
        ? hydratePersistedTableState(
            initialState,
            parsedInitialState,
            protectedFilterKeys,
            metadataColumns,
            metadataFilterKeys,
          )
        : freshInitialState,
    [
      freshInitialState,
      hasCompatibleDraft,
      initialState,
      metadataColumns,
      metadataFilterKeys,
      parsedInitialState,
      protectedFilterKeys,
    ],
  );

  const [currentTableState, setTableState] = useReducer(
    filterReducer,
    definedInitialState,
  );

  // workspaceModifiedAt is deliberately excluded: a workspace refetch can bump
  // modified_at without a user-visible change (e.g. a Vivo templated workspace
  // touched server-side), and rehydrating on it would clobber in-session state
  // such as the active screen view. Content updates reach the state through
  // explicit paths instead: saving re-stamps the meta and template drift
  // surfaces the "apply latest workspace" banner.
  const hydrationSignature = [
    storageKey,
    initialState.workspaceId ?? "no-workspace",
    metadataColumns?.join("|") ?? "no-columns",
  ].join(":");
  const appliedHydrationSignatureRef = useRef(hydrationSignature);
  const pendingHydrationRef = useRef(false);

  useEffect(() => {
    if (appliedHydrationSignatureRef.current === hydrationSignature) return;
    appliedHydrationSignatureRef.current = hydrationSignature;
    pendingHydrationRef.current = true;
    setTableState({
      type: "reset",
      initialState: definedInitialState,
    });
  }, [definedInitialState, hydrationSignature]);

  useEffect(() => {
    if (savedInitialState && !hasCompatibleDraft) {
      localStorage.removeItem(storageKey);
    }
  }, [hasCompatibleDraft, savedInitialState, storageKey]);

  const initialColumns = initialState.columns;

  const setColumn = useCallback((columns: ColumnsType[]) => {
    setTableState({
      type: "setColumn",
      columns: columns,
    });
  }, []);

  const setSorting = (sorting: SortItem[]) => {
    setTableState({
      type: "setSorting",
      sorting: sorting,
    });
  };

  const setFilters = (filters: any) => {
    if (protectedFilterKeys.length === 0) {
      // No protected filters, use filters as-is
      setTableState({
        type: "setAllFilters",
        filters: filters,
      });
    } else {
      // Preserve protected filters from current state
      const currentProtectedFilters = Object.fromEntries(
        Object.entries(currentTableState.filters || {}).filter(([key]) =>
          protectedFilterKeys.includes(key),
        ),
      );
      const mergedFilters = {
        ...currentProtectedFilters,
        ...filters,
      };
      setTableState({
        type: "setAllFilters",
        filters: mergedFilters,
      });
    }
  };

  const setFilter = (filter: any) => {
    setTableState({
      type: "setFilter",
      filters: filter,
    });
  };

  const setPlots = (plots: VivoPlotProps[]) => {
    setTableState({
      type: "setPlots",
      plots: plots,
    });
  };

  const setPlotLayoutsByBreakpoint = (
    plotLayoutsByBreakpoint: PlotLayoutsByBreakpoint,
  ) => {
    setTableState({
      type: "setPlotLayoutsByBreakpoint",
      plotLayoutsByBreakpoint,
    });
  };

  const setPagination = (pagination: any) => {
    setTableState({
      type: "setPagination",
      pagination: pagination,
    });
  };

  const setScreenView = (screenView: string) => {
    setTableState({
      type: "setScreenView",
      screenView: screenView,
    });
  };

  const reset = (overrideState?: TableState, callback?: () => void) => {
    setTableState({
      type: "reset",
      initialState: {
        ...initialState,
        ...overrideState,
        pagination: {
          ...initialState?.pagination,
          ...overrideState?.pagination,
          total: currentTableState.pagination.total, // Preserve current total until API updates it
        },
        version: currentFilterVersion,
      },
    });
    callback?.();
  };

  const setLockedColumns = (lockedColumns: any) => {
    setTableState({
      type: "setLockedColumns",
      lockedColumns: lockedColumns,
    });
  };

  useEffect(() => {
    if (pendingHydrationRef.current) {
      if (!isEqual(currentTableState, definedInitialState)) {
        return;
      }

      pendingHydrationRef.current = false;
    }

    const stateToSave = createPersistedTableDraft(currentTableState);
    const freshStateToPersist = createPersistedTableDraft(freshInitialState);
    const hasUnsavedDraft = !isEqual(stateToSave, freshStateToPersist);

    if (!hasKnownUser) return;

    if (!hasUnsavedDraft) {
      localStorage.removeItem(storageKey);
      return;
    }
    try {
      localStorage.setItem(storageKey, JSON.stringify(stateToSave));
      /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
    } catch (e) {
      console.warn(
        `[useTableState] localStorage quota exceeded for "${storageKey}", clearing and retrying...`,
      );
      localStorage.removeItem(storageKey);
      try {
        localStorage.setItem(storageKey, JSON.stringify(stateToSave));
      } catch (e2) {
        console.error(
          `[useTableState] Failed to save even after clearing key:`,
          e2,
        );
      }
    }
  }, [
    definedInitialState,
    freshInitialState,
    hasKnownUser,
    storageKey,
    currentTableState,
  ]);

  useEffect(() => {
    if (currentTableState.columns.length === 0 && initialColumns.length > 0) {
      setColumn(initialColumns);
    }
  }, [initialColumns, currentTableState.columns?.length, setColumn]);

  const clearLocalStorage = () => {
    localStorage.removeItem(storageKey);
  };

  const setWorkspaceMeta = (
    workspaceId: number,
    workspaceModifiedAt: string,
  ) => {
    setTableState({
      type: "setWorkspaceMeta",
      workspaceId,
      workspaceModifiedAt,
    });
  };

  const tableStateFunctions = {
    setPlots,
    setPlotLayoutsByBreakpoint,
    setFilter,
    setFilters,
    setColumn,
    setSorting,
    reset,
    clearLocalStorage,
    setLockedColumns,
    setPagination,
    setScreenView,
    setWorkspaceMeta,
  };

  return {
    currentTableState,
    tableStateFunctions,
  };
};

export default useTableState;
